import React, { useContext, useRef, useState } from 'react';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import LoadedOptionsTypeahead from '../../muiComponents/LoadedOptionsTypeahead';
import { validateForm } from '../../utils/validateForms';
import { ContextUserProfile } from '../../contexts/UserProfileContext';
import { ContextSettings } from '../../contexts/SettingsContext';
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
} from '@mui/material';
import { languagesMenu } from '../../utils/menusData';
import { findIana } from 'windows-iana';
import useTranslate from '../../customHooks/useTranslate';
import lightThemeIcon from '../../images/themes/theme-light.svg';
import darkThemeIcon from '../../images/themes/theme-dark.svg';
import systemThemeIcon from '../../images/themes/theme-system.svg';

import useTheme from '../../customHooks/useTheme';
import { ThemeTypes } from '../../types/theme/themeTypes';
import { setLSTheme } from '../../utils/userProfileContextHelpers';

const EditionContent = () => {
    const {
        timeZoneOptions,
        setIsEditionMode,
        selectedTimezone,
        setIsLoading,
    } = useContext(ContextSettings);
    const { settings, setSettings, setTimeZone, setLanguage, saveSettings } =
        useContext(ContextUserProfile);
    const { setTheme } = useTheme();
    const translate = useTranslate();
    const [errors, setErrors] = useState({});
    const [isValidated, setIsValidated] = useState(false);
    const initialState = useRef({ ...settings });

    const fieldsToValidate = {
        language: { value: settings.language, validators: ['requiredString'] },
        timeZone: {
            value: settings.timeZone,
            validators: ['requiredString'],
        },
    };

    const handleValidate = () => {
        if (isValidated) {
            const validationErrors = validateForm(fieldsToValidate);
            let newErrors = {};
            for (let newError of validationErrors) {
                newErrors[newError.inputKey] = newError.errorMessage;
            }
            setErrors(newErrors);
        }
    };

    const handleSend = async () => {
        setIsValidated(true);
        const validationErrors = validateForm(fieldsToValidate);
        let newErrors = {};
        for (let newError of validationErrors) {
            newErrors[newError.inputKey] = newError.errorMessage;
        }
        setErrors(newErrors);
        const isValid = validationErrors.length === 0;
        if (isValid) {
            setIsLoading(true);
            await saveSettings({ ...settings }, initialState.current);
            setIsLoading(false);
        }
    };

    const handleChangeTheme = e => {
        const themeKey = e.target.value;
        setTheme(themeKey);
    };

    return (
        <>
            <Grid container rowSpacing={{ xs: 1, md: 4 }} columns={1}>
                <Grid item xs={1}>
                    <LoadedOptionsTypeahead
                        labelText={'language'}
                        errorMessage={errors.language}
                        id={'language'}
                        options={languagesMenu}
                        optionsLabelKey={'name'}
                        idValue={
                            languagesMenu.find(
                                item => item.text === settings.language
                            ).Id
                        }
                        handleChange={(e, value) => {
                            if (value) {
                                setLanguage(value.text);
                            }
                        }}
                        onBlur={handleValidate}
                        required
                    />
                </Grid>
                <Grid item xs={1}>
                    <LoadedOptionsTypeahead
                        labelText={'time_zone'}
                        id={'timeZone'}
                        options={timeZoneOptions}
                        idValue={selectedTimezone.Id}
                        optionsLabelKey={'DisplayName'}
                        handleChange={(e, value) => {
                            const [IANA] = findIana(value?.StandardName);
                            if (IANA) {
                                setTimeZone(IANA);
                            }
                        }}
                        onBlur={handleValidate}
                        errorMessage={errors.timeZone}
                        required
                    />
                </Grid>
                <Grid item xs={1}>
                    <FormControl>
                        <FormLabel id='demo-row-radio-buttons-group-label'>
                            {translate('theme')}
                        </FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby='theme'
                            name='theme'
                            className='flex gap-30'
                            value={settings.theme}
                            onChange={handleChangeTheme}
                        >
                            <div className='flex-column gap-10'>
                                <FormControlLabel
                                    value={ThemeTypes.LIGHT}
                                    control={<Radio />}
                                    label={translate('light')}
                                />
                                <img src={lightThemeIcon} alt='light theme' />
                            </div>
                            <div className='flex-column gap-10'>
                                <FormControlLabel
                                    value={ThemeTypes.DARK}
                                    control={<Radio />}
                                    label={translate('dark')}
                                />
                                <img src={darkThemeIcon} alt='dark theme' />
                            </div>
                            <div className='flex-column gap-10'>
                                <FormControlLabel
                                    value={ThemeTypes.SYSTEM}
                                    control={<Radio />}
                                    label={translate('system')}
                                />
                                <img src={systemThemeIcon} alt='system theme' />
                            </div>
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </Grid>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button
                    variant='outlined'
                    onClick={() => {
                        setIsEditionMode(false);
                        setSettings(prev => {
                            return {
                                ...prev,
                                ...initialState.current,
                            };
                        });
                        setLSTheme(initialState.current.theme);
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    onClick={async () => {
                        await handleSend();
                        setIsEditionMode(false);
                    }}
                >
                    Update
                </Button>
            </CardActions>
        </>
    );
};

export default EditionContent;
